import { FC } from 'react';
import { Button, Box, Typography, Dialog } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useAbility } from '@casl/react';
import { KeyedMutator } from 'swr';
import palette from '../../theme/palette';
import Rk9Api from '../../dataServices/Rk9Api';
import { PUT } from '../../constants/requests';
import { AbilityContext } from '../../context/AbilityContext';
import { User } from '../../types/User';

interface ResetPasswordDialogProps {
  open: boolean;
  clientToResetPassword: User | null;
  setClientToResetPassword: (client: User | null) => void;
  close: () => void;
  mutate: KeyedMutator<User[]>;
}

export const ResetPasswordDialog: FC<ResetPasswordDialogProps> = (props) => {
  const { open, clientToResetPassword, setClientToResetPassword, close, mutate } = props;
  const ability = useAbility(AbilityContext);
  const { enqueueSnackbar } = useSnackbar();

  const resetClientPassword = async () => {
    const resetPassword = 'password';
    if (
      clientToResetPassword &&
      clientToResetPassword.id &&
      resetPassword &&
      ability.can('update', 'All')
    ) {
      const updatedClient = { ...clientToResetPassword };
      updatedClient.password = resetPassword;

      await Rk9Api(PUT, `/users/admin/${clientToResetPassword.id}`, updatedClient)
        .then(() => {
          enqueueSnackbar(`Client's password was reset to "password"!`, {
            persist: false,
            variant: 'success',
          });
        })
        .catch(() =>
          enqueueSnackbar(
            "There was a problem resetting the client's password. Please let someone know!",
            {
              persist: false,
              variant: 'error',
            },
          ),
        );
      await mutate();
      close();
    }
  };

  const cancelResetClientPassword = () => {
    setClientToResetPassword(null);
    close();
  };

  return (
    clientToResetPassword && (
      <Dialog open={open} fullWidth maxWidth="sm" onClose={cancelResetClientPassword}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '30px',
          }}
        >
          <Typography variant="h4" style={{ marginBottom: '20px', textAlign: 'center' }}>
            <span style={{ display: 'block' }}>Are you sure you want to reset</span>
            <span style={{ display: 'block' }}>
              {clientToResetPassword.name} ({clientToResetPassword.username})&apos;s password?
            </span>
          </Typography>
          <Typography variant="h6" style={{ marginBottom: '20px' }}>
            Their password will be reset to &quot;password&quot; if so.
          </Typography>
          <Box>
            <Button variant="outlined" onClick={cancelResetClientPassword}>
              Cancel
            </Button>
            <Button
              onClick={resetClientPassword}
              style={{
                backgroundColor: palette.selected.primary,
                color: palette.white,
                marginLeft: '20px',
              }}
              disabled={!ability.can('update', 'All')}
            >
              Reset Password
            </Button>
          </Box>
        </Box>
      </Dialog>
    )
  );
};

export default ResetPasswordDialog;
